import { Fragment, ReactNode, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "components/ui/combobox";
import type { ComboboxData, Option } from "components/ui/combobox/types";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { useDebouncedValue } from "lib/debounce";
import { useSearchFrameworks } from "lib/hooks/api/frameworks/useSearchFrameworks";
import { routes } from "lib/routes";
import { SearchRequestSortOrder } from "lib/types/graphQLEnums";
import { ComboboxProps } from "../../combobox/ComboBox";
import { EntityOption } from "./ComboboxOption";
import { EmptyComboboxTrigger } from "./EmptyComboboxTrigger";
import { SelectedEntityCard } from "./SelectedEntityCard";

export type FrameworkComboboxProps<TFieldValues extends FieldValues = FieldValues> = Required<
  Pick<ComboboxProps<TFieldValues>, "control" | "name">
>;

const DEFAULT_FRAMEWORK_SELECT_FILTERS = {
  sort: "title",
  limit: 50,
  page: 1,
  sortOrder: SearchRequestSortOrder.Asc,
};

function ComboboxContentComponent(props: {
  combobox: ComboboxData;
  setIsOpen: (open: boolean) => void;
  setSearchText: (text: string) => void;
  disabled?: boolean;
  loading: boolean;
  trigger?: ReactNode;
}) {
  const { trigger, combobox, setIsOpen, setSearchText, disabled, loading } = props;
  return (
    <ComboboxContent
      open={combobox.open}
      setOpen={(open: boolean) => {
        combobox.setOpen(open);
        setIsOpen(open);
      }}
      trigger={trigger ? trigger : <div />}
    >
      <ComboboxInput
        placeholder={"Search frameworks..."}
        filterValue={combobox.filterValue}
        setFilterValue={(value) => {
          combobox.setFilterValue(value);
          setSearchText(value);
        }}
        open={combobox.open}
        setOpen={combobox.setOpen}
        value={[]}
        items={combobox.items}
        multiple={combobox.multiple}
        inputRef={combobox.inputRef}
        inputElementRef={combobox.inputElementRef}
        disabled={disabled}
      />
      <ComboboxEmpty
        filteredItems={combobox.filteredItems}
        filterValue={combobox.filterValue}
        disableAddingOptions
      >
        {loading ? <SkeletonText width="100%" /> : "No frameworks found."}
      </ComboboxEmpty>

      <ListScrollArea>
        <ComboboxList items={combobox.items} filteredItems={combobox.filteredItems}>
          <ComboboxCollection options={combobox.filteredItems as Option[]}>
            {(option: Option) => {
              return (
                <ComboboxItem
                  key={String(option.value)}
                  option={option}
                  value={combobox.value}
                  selectItem={combobox.selectItem}
                  multiple={combobox.multiple}
                  highlightedIndex={combobox.highlightedIndex}
                  filteredItems={combobox.filteredItems}
                >
                  <EntityOption label={option.label} dataType="framework" />
                </ComboboxItem>
              );
            }}
          </ComboboxCollection>
        </ComboboxList>
      </ListScrollArea>
    </ComboboxContent>
  );
}

export function FrameworkCombobox<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
}: FrameworkComboboxProps<TFieldValues>) {
  const { field } = useController({ control, name });
  const [searchText, setSearchText] = useState("");
  const [debouncedText] = useDebouncedValue(searchText, 500);
  const [isOpen, setIsOpen] = useState(false);
  const [clickedFrameworkId, setClickedFrameworkId] = useState<string | null>(null);

  const enableInitialSearch =
    !!field.value && !debouncedText && !!(field.value as string[])?.length && !isOpen;

  const { data: initialFrameworks, isLoading: isInitialLoading } = useSearchFrameworks(
    { frameworkIds: field.value, ...DEFAULT_FRAMEWORK_SELECT_FILTERS },
    false,
    {
      enabled: enableInitialSearch,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const { data, isFetching: isSearchLoading } = useSearchFrameworks(
    {
      searchText: debouncedText,
      ...DEFAULT_FRAMEWORK_SELECT_FILTERS,
    },
    false,
    {
      enabled: !enableInitialSearch,
    },
  );

  const getInitialFramework = (guid: string) => {
    if (!guid) return null;
    return initialFrameworks?.results.find((framework) => framework.id === guid) || null;
  };

  const getFrameworkInSearchResults = (guid: string) => {
    if (!guid) return null;
    return data?.results.find((framework) => framework.id === guid) || null;
  };

  const getSelectedFramework = (guid: string) => {
    return getFrameworkInSearchResults(guid) || getInitialFramework(guid);
  };

  const getOptions = () => {
    if (isSearchLoading) return [];
    const options: Option[] = data?.results
      ? data.results.map((framework) => ({
          label: framework.title,
          value: framework.id,
        }))
      : [];
    if (!debouncedText && initialFrameworks?.results && options.length === 0) {
      return initialFrameworks.results.map((framework) => ({
        label: framework.title,
        value: framework.id,
      }));
    }
    const unassignedOption: Option = {
      label: "No Framework",
      value: "",
    };
    options.unshift(unassignedOption);
    return options;
  };

  const options = getOptions();

  const arrayValue = field.value === null ? [""] : field.value ? [field.value] : [];

  const isLoading = enableInitialSearch
    ? isInitialLoading && !initialFrameworks
    : isSearchLoading && !data;

  return (
    <Combobox
      items={options}
      value={arrayValue}
      onChange={(values) => {
        // Convert array back to single value
        const newValue = values.length > 0 ? values[0] : null;
        field.onChange(newValue === "" ? null : newValue);
      }}
      multiple={false}
      externalFilter={!isLoading}
    >
      {(combobox) => {
        const selectedValue = combobox.value[0];
        const selectedFramework =
          selectedValue && selectedValue !== ""
            ? getSelectedFramework(String(selectedValue))
            : null;

        return (
          <PropertyInnerContainer>
            <Flex column gap={8} width="100%" minWidth={0}>
              {selectedFramework ? (
                <Fragment key={selectedFramework.id}>
                  <SelectedEntityCard
                    entity={{
                      id: String(selectedFramework?.id || ""),
                      name: selectedFramework?.title || "",
                    }}
                    dataType="framework"
                    combobox={combobox}
                    setIsOpen={setIsOpen}
                    setEntityClickerId={setClickedFrameworkId}
                    clickedEntityId={clickedFrameworkId}
                    linkButtonProps={{
                      href: `${routes.frameworks}/${selectedFramework.id}`,
                      paywall: {
                        featureType: "FRAMEWORKS",
                        contextSource: "Framework filter",
                      },
                    }}
                  />
                  <ComboboxContentComponent
                    combobox={combobox}
                    setIsOpen={setIsOpen}
                    setSearchText={setSearchText}
                    disabled={isLoading}
                    loading={isLoading}
                  />
                </Fragment>
              ) : (
                <ComboboxContentComponent
                  combobox={combobox}
                  setIsOpen={setIsOpen}
                  setSearchText={setSearchText}
                  disabled={isLoading}
                  loading={isLoading}
                  trigger={
                    <EmptyComboboxTrigger
                      label="Framework"
                      disabled={isLoading}
                      onClick={() => {
                        combobox.setOpen(true);
                        setIsOpen(true);
                      }}
                    />
                  }
                />
              )}
            </Flex>
          </PropertyInnerContainer>
        );
      }}
    </Combobox>
  );
}

const PropertyInnerContainer = styled.div({
  minWidth: 0,
  width: "100%",
});

const ListScrollArea = styled.div({
  maxHeight: 390,
  overflowY: "auto",
});
